import React, { useState } from 'react';
import axios from 'axios';
import { 
    FaSave, FaKey, FaCog, FaEnvelope, 
    FaIdCard, FaLink, FaCheckCircle, FaExclamationTriangle, FaUserTie 
} from 'react-icons/fa';

const SettingsPage = ({ user, onUpdateUser }) => {
    // État initial du formulaire basé sur les props utilisateur
    const [formData, setFormData] = useState({
        fullName: user.fullName || '',
        bio: user.bio || '',
        avatar: user.avatar || '',
        newPassword: ''
    });
    const [message, setMessage] = useState(null);

    // Fonction pour générer les initiales si l'avatar est absent
    const getInitials = (name) => {
        return name ? name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2) : 'U';
    };

    // Gestion des changements dans les champs de saisie
    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    // Envoi des données au backend
    const handleSave = async (e) => {
        e.preventDefault();
        try {
            const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
            const storageData = JSON.parse(localStorage.getItem('facsmart_user') || '{}');
            const token = storageData.token; 
            const config = { headers: { Authorization: `Bearer ${token}` } };

            const res = await axios.put(`${API_BASE}/auth/update`, {
                userId: user.id,
                ...formData
            }, config);
            
            // Mise à jour du token si le serveur en renvoie un nouveau
            if (res.data.token) {
                storageData.token = res.data.token;
                localStorage.setItem('facsmart_user', JSON.stringify(storageData));
            }
            
            // Notification de succès et mise à jour de l'état global
            if(res.data.user) {
                const safeUpdatedUser = { ...user, ...res.data.user };
                onUpdateUser(safeUpdatedUser);
                setMessage({ type: 'success', text: "Profil mis à jour avec succès !" });
                setFormData(prev => ({...prev, newPassword: ''}));
                setTimeout(() => setMessage(null), 4000);
            }
        } catch (err) {
            console.error(err);
            const errorText = err.response?.status === 401 
                ? "Session expirée. Veuillez vous reconnecter." 
                : "Erreur lors de la mise à jour.";
            setMessage({ type: 'error', text: errorText });
            setTimeout(() => setMessage(null), 4000);
        }
    };

    return (
        <div className="container-fluid py-4 bg-light min-vh-100">
            {/* EN-TÊTE */}
            <div className="mb-4">
                <h3 className="fw-bold text-dark text-uppercase">
                    <FaCog className="me-2 text-primary" /> Mon Profil
                </h3>
                <p className="text-muted">Gérer vos informations personnelles et votre sécurité.</p>
            </div>

            {/* ALERTES */}
            {message && (
                <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-danger'} d-flex align-items-center shadow-sm border-0`} role="alert">
                    {message.type === 'success' ? <FaCheckCircle className="me-2"/> : <FaExclamationTriangle className="me-2"/>}
                    <div>{message.text}</div>
                </div>
            )}
            
            <div className="row g-4">
                {/* APERÇU DU PROFIL */}
                <div className="col-12 col-lg-4">
                    <div className="card border-0 shadow-sm text-center p-4 h-100">
                        <div className="mx-auto rounded-circle bg-primary text-white d-flex align-items-center justify-content-center mb-3 shadow" 
                             style={{ width: '120px', height: '120px', overflow: 'hidden', fontSize: '2.5rem' }}>
                            {formData.avatar ? (
                                <img src={formData.avatar} alt="Avatar" className="w-100 h-100 object-fit-cover" />
                            ) : (
                                getInitials(formData.fullName || user.username)
                            )}
                        </div>
                        <h4 className="fw-bold text-dark mb-1">{formData.fullName || user.username}</h4>
                        <p className="text-muted mb-3">{formData.bio || 'Aucune fonction renseignée'}</p>
                        <span className="badge bg-primary bg-opacity-10 text-primary rounded-pill px-3 py-2 text-uppercase">
                            {user.role}
                        </span>
                    </div>
                </div>

                {/* FORMULAIRE DE MODIFICATION */}
                <div className="col-12 col-lg-8">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-header bg-white py-3">
                            <h6 className="fw-bold mb-0 text-primary">Informations du compte</h6>
                        </div>
                        <div className="card-body p-4">
                            <form onSubmit={handleSave}>
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label small fw-bold text-muted">NOM COMPLET</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-light border-0"><FaIdCard className="text-muted"/></span>
                                            <input type="text" className="form-control border-0 bg-light" name="fullName" value={formData.fullName} onChange={handleChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label small fw-bold text-muted">EMAIL (NON MODIFIABLE)</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-light border-0"><FaEnvelope className="text-muted"/></span>
                                            <input type="text" className="form-control border-0 bg-light" value={user.username} disabled />
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label small fw-bold text-muted">FONCTION / POSTE</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-light border-0"><FaUserTie className="text-muted"/></span>
                                            <input type="text" className="form-control border-0 bg-light" name="bio" value={formData.bio} onChange={handleChange} />
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label small fw-bold text-muted">LIEN PHOTO DE PROFIL</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-light border-0"><FaLink className="text-muted"/></span>
                                            <input type="text" className="form-control border-0 bg-light" name="avatar" value={formData.avatar} onChange={handleChange} />
                                        </div>
                                    </div>
                                    <div className="col-12 py-3">
                                        <hr className="text-muted opacity-25" />
                                    </div>
                                    <div className="col-12">
                                        <div className="p-3 rounded bg-warning bg-opacity-10 border border-warning border-opacity-25">
                                            <h6 className="fw-bold text-warning mb-2"><FaKey className="me-2"/> Sécurité</h6>
                                            <label className="form-label small fw-bold text-muted">NOUVEAU MOT DE PASSE</label>
                                            <input type="password" className="form-control" name="newPassword" value={formData.newPassword} onChange={handleChange} placeholder="Laisser vide pour ne pas changer" />
                                        </div>
                                    </div>
                                    <div className="col-12 text-end mt-4">
                                        <button type="submit" className="btn btn-primary px-4 py-2 fw-bold shadow-sm">
                                            <FaSave className="me-2"/> Enregistrer les modifications
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SettingsPage;